import { useState, useEffect } from 'react';
import api from './api/axiosInstance.js';
import CompanyAddHr from './CompanyAddHr';
import CompanyAddJobs from './CompanyAddJobs';
import CompanyAddJobNumbers from './CompanyAddJobNumbers.js';
import CompanyAddAreasDirections from './CompanyAddAreasDirections.js';
import { Row, Col, Container, Card, Form } from 'react-bootstrap';
import CompanyAddGeneral from './CompanyAddGeneral';

const CompanyAddStep2 = ({ id, formData, handleChange, areasData, handleAreasChange, setAreasData }) => {

    const [savedModalTitle, setSavedModalTitle] = useState('Update successful');
    const [savedModalMessage, setSavedModalMessage] = useState('General company information was updated successfully.');
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [logoPreview, setLogoPreview] = useState('');
    const [forceRerenderKey, setForceRerenderKey] = useState(0);

    const [generalData, setGeneralData] = useState({
        logo: '',
        logoPreview: '',
        title: '',
        bestPlacetoWorkRating: '',
        accessLevel: '',
        url: '',
        bestPlacetoWorkDescription: '',
    });

    const [statusMessage, setStatusMessage] = useState('');
    const [originalLogoUrl, setOriginalLogoUrl] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [activityStatus, setActivityStatus] = useState(true);
    const [logo, setLogo] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Fetch company data for editing
        console.log('CompanyAddStep2 id:', id);

        const fetchCompanyData = async () => {
            try {
                const response = await api.get(`/api/companies/${id}`);

                const companyData = response.data;

                setActivityStatus(companyData.activityStatus ?? false); // ✅ Load activity status

                setGeneralData({
                    title: companyData.title || '',
                    url: companyData.url || '',
                    logo: companyData.logo || '',
                    logoPreview: companyData.logo || '',
                    accessLevel: companyData.accessLevel || '',
                    bestPlacetoWorkRating: companyData.bestPlacetoWorkRating || '',
                    bestPlacetoWorkDescription: companyData.bestPlacetoWorkDescription || '',
                    activityStatus: companyData.activityStatus ?? false,
                    createdAt: companyData.created_at || '',
                    updatedAt: companyData.updatedAt || '',
                    accountApprovalStatus: companyData.accountApprovalStatus || '',
                });
            } catch (error) {
                console.error('Error fetching company:', error);
            }
        };

        fetchCompanyData();
    }, [id]);

    const validateGeneralFields = () => {
        const newErrors = {};

        if (!generalData.title.trim()) {
            newErrors.title = 'Company name is required.';
        }
        if (!generalData.url.trim()) {
            newErrors.url = 'Company website is required.';
        } else {
            const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
            if (!urlPattern.test(generalData.url.trim())) {
                newErrors.url = 'Please enter a valid URL.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleResetLogo = () => {
        setLogo(null);
        setLogoPreview(originalLogoUrl); // restore saved logo from DB
    };

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;

        // ✅ Handle file separately
        if (name === 'logo' && value instanceof File) {
            setLogo(value);
            const fileUrl = URL.createObjectURL(value);
            setLogoPreview(fileUrl);

            setGeneralData((prevData) => ({
                ...prevData,
                logo: value,
                logoPreview: fileUrl,
            }));
            return;
        }

        setGeneralData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!validateGeneralFields()) return false;

        try {
            const data = new FormData();
            Object.keys(generalData).forEach((key) => {
                if (key === 'logo') {
                    if (logo) {
                        data.append('logo', logo);
                    }
                } else {
                    data.append(key, generalData[key]);
                }
            });

            const res = await api.put(`/api/companies/${id}/general`, data);

            const updated = res.data;
            setGeneralData((prev) => ({
                ...prev,
                ...updated,
                logoPreview: updated.logo || '',
            }));
            setLogoPreview(updated.logo || '');
            setLogo(null);
            setSavedModalTitle('Update successful');
            setSavedModalMessage('General company information was updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (error) {
            console.error('Error updating general information:', error);
            alert('An error occurred while updating general information.');
            return false;
        }
    };

    const handleDeleteImage = () => {
        setLogo(null);
        setLogoPreview('fallback'); // we'll handle it in the child
        setGeneralData((prev) => ({
            ...prev,
            logo: '',
            logoPreview: 'fallback',
        }));
    };

    return (
        <>
            <Col className="mb-4">
                <Row className="mb-4">
                    <Col>
                        <Form onSubmit={handleGeneralSubmit}>
                            <CompanyAddGeneral
                                formData={generalData}
                                handleChange={handleGeneralChange}
                                logo={generalData.logoPreview}
                                errors={errors}
                                handleGeneralSubmit={handleGeneralSubmit}
                                isEditPage={true}
                                activityStatus={activityStatus}
                                handleResetLogo={handleResetLogo}
                                forceRerenderKey={forceRerenderKey}
                                setForceRerenderKey={setForceRerenderKey}
                                handleDeleteImage={handleDeleteImage}
                            />
                        </Form>
                    </Col>
                </Row>
                <Row className="mb-5">
                    <Col><h2>Hiring info</h2></Col>
                </Row>
                <Row>
                    <Col>
                        <CompanyAddHr
                            formData={formData}
                            handleChange={handleChange}
                            isEditPage={false}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <CompanyAddJobs
                            formData={formData}
                            handleChange={handleChange}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <CompanyAddJobNumbers
                            formData={formData}
                            handleChange={handleChange}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <CompanyAddAreasDirections
                            formData={areasData}
                            handleChange={handleAreasChange}
                            setFormData={setAreasData}
                            isEditPage={false}
                        />
                    </Col>
                </Row>
                
            </Col>
        </>
    );
};


export default CompanyAddStep2;
